/**
 * TypeScript types for Medication Orders
 */

export enum MedicationOrderStatus {
    PENDING = 'PENDING',
    ACTIVE = 'ACTIVE',
    PARTIALLY_RECEIVED = 'PARTIALLY_RECEIVED',
    RECEIVED = 'RECEIVED',
    CANCELLED = 'CANCELLED',
    ON_HOLD = 'ON_HOLD',
    EXPIRED = 'EXPIRED',
    ORDER_MAIL_SENT = 'ORDER_MAIL_SENT',
}

export enum MedicationRoute {
    // Enteral Routes
    PO = 'PO',
    SL = 'SL',
    BUCCAL = 'BUCCAL',
    NG_GT = 'NG_GT',
    JT = 'JT',
    PR = 'PR',

    // Parenteral (Injection) Routes
    IV = 'IV',
    IM = 'IM',
    SC = 'SC',
    ID = 'ID',
    IO = 'IO',
    IT = 'IT',
    IA = 'IA',
    IP = 'IP',

    // Topical & Skin Routes
    TOP = 'TOP',
    TD = 'TD',
    INTRADERM = 'INTRADERM',
    WOUND = 'WOUND',
    OTIC = 'OTIC',
    OPHTH = 'OPHTH',

    // Inhalation / Respiratory Routes
    INH = 'INH',
    NEB = 'NEB',
    NASAL = 'NASAL',

    // Mucosal Routes
    VAG = 'VAG',
    URETHRAL = 'URETHRAL',
    RECTAL = 'RECTAL',

    // Other Specialized Routes
    EPIDURAL = 'EPIDURAL',
    INTRACARDIAC = 'INTRACARDIAC',
    INTRAOCULAR = 'INTRAOCULAR',
    INTRAPLEURAL = 'INTRAPLEURAL',
    INTRAVESICAL = 'INTRAVESICAL',
}

export interface MedicationItemDTO {
    id?: string;
    medicationOrderId?: string;
    medicationName: string;
    genericName?: string;
    dosage: string;
    route: MedicationRoute;
    frequency: string;
    startDate: string;
    endDate?: string;
    indication?: string;
    specialInstructions?: string;
    sideEffectsToMonitor?: string;
    isPrn?: boolean;
    prnReason?: string;
    pharmacyName?: string;
    pharmacyPhone?: string;
    refillsAuthorized?: number;
    quantity?: string;
    receivedQuantity?: number;
    remainingQuantity?: number;
    updatedStock?: number | string;
    notes?: string;
    medicationExpirationDate?: string;
    isNarcotic?: boolean;
    isCritical?: boolean;
}

export interface MedicationOrderDTO {
    id?: string;
    orderNumber?: string;
    residentId: string;
    residentName?: string;
    residentFullName?: string;
    prescribingPhysicianId?: string;
    prescribingPhysicianName: string;
    medicationItems: MedicationItemDTO[];
    startDate?: string;
    endDate?: string;
    status?: MedicationOrderStatus;
    orderedBy?: string;
    orderedAt?: string;
    approvedBy?: string;
    approvedAt?: string;
    discontinuedBy?: string;
    discontinuedAt?: string;
    discontinuationReason?: string;
    notes?: string;
    tenantId?: string;
    facilityId?: string;
    pharmacyName?: string;
    pharmacyEmail?: string;
    pharmacyContactNumber?: string;
    createdAt?: string;
    updatedAt?: string;
    updatedBy?: string;
    createdByName?: string;
    updatedByName?: string;
    canBeModified?: boolean;
    isActive?: boolean;
    prescriptionNumber?: string;
    prescriptionFileName?: string;
    prescriptionFileType?: string;
    prescriptionFileData?: string;
}

export interface CreateMedicationOrderRequest {
    residentId: string;
    prescribingPhysicianName: string;
    medicationItems: MedicationItemDTO[];
    startDate?: string;
    endDate?: string;
    notes?: string;
    facilityId?: string;
    orderedBy?: string;
    pharmacyName?: string;
    pharmacyEmail?: string;
    pharmacyContactNumber?: string;
    prescriptionNumber?: string;
    prescribingPhysicianId?: string;
}

export interface PageResponse<T> {
    content: T[];
    totalElements: number;
    totalPages: number;
    size: number;
    number: number;
    first: boolean;
    last: boolean;
    empty: boolean;
}

export interface MedicationOrderStatusCount {
    [key: string]: number;
}

export interface MonthlyMARDose {
    id?: string;
    apiId?: string;
    date?: string;
    dateTime?: string;
    status: string;
    colorCode?: string;
    administeredBy?: string;
    administeredAt?: string;
    notes?: string;
    createdByName?: string;
    updatedByName?: string;
    lowStock?: boolean;
}


export interface MonthlyMARSlot {
    doseNumber: string;
    slotTime: string;
    records: MonthlyMARDose[];
}

export interface MonthlyMARRecord {
    medicationName: string;
    startDate?: string;
    endDate?: string;
    dose?: string;
    route?: string;
    schedule?: string;
    doses: MonthlyMARSlot[];
    updatedStock?: number | string;
    isNarcotic?: boolean;
    isCritical?: boolean;
}

export interface ReceiveMedicationOrderItem {
    itemId: string;
    receivedQuantity: number;
}

export interface ReceiveMedicationOrderRequest {
    items?: ReceiveMedicationOrderItem[];
    notes?: string;
}
